<template>
    <div class="relative">
        <ListItemButton icon-name="quote-24px" dusk="citationButton" @click="toggleCitationMenu">
            <span>{{ localizations[lang].tooltips.citation }}</span>
        </ListItemButton>

        <!-- Desktop: Dropdown -->
        <transition name="slide-fade">
            <div
                v-show="isCitationMenuOpen && !isMobileModalOpen"
                class="absolute -left-[250px] -top-16 z-[60] hidden md:block"
            >
                <ListDropdown>
                    <template v-for="format in availableCitationFormats">
                        <ChicagoSubmenuDropdown
                            v-if="format === 'Chicago'"
                            :key="format"
                            ref="chicagoDesktopDropdown"
                            :subformats="chicagoSubformats"
                            :labels="localizations[lang].citation.buttons"
                            dusk-prefix="citationFormat-"
                            submenu-position-class="right-full mr-52"
                            chevron-position="left"
                            @select="generateCitation"
                        />
                        <!-- Regular citation formats -->
                        <ListItemButton
                            v-else
                            :key="format"
                            :dusk="`citationFormat-${format}`"
                            class="text-neutral-500 dark:text-neutral-300"
                            @click="generateCitation(format)"
                        >
                            {{ localizations[lang].citation.buttons[format] }}
                        </ListItemButton>
                    </template>
                </ListDropdown>
            </div>
        </transition>

        <!-- Mobile: Modal -->
        <CitationMobileModal
            :is-open="isMobileModalOpen"
            :title="localizations[lang].tooltips.citation"
            :formats="availableCitationFormats"
            :chicago-subformats="chicagoSubformats"
            :chicago-expanded="showMobileChicagoSubmenu"
            :labels="localizations[lang].citation.buttons"
            @close="closeCitationMenu"
            @toggle-chicago="toggleMobileChicagoSubmenu"
            @select="generateCitation"
        />
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import ListItemButton from './list/ListItemButton.vue';
import ListDropdown from './list/ListDropdown.vue';
import ChicagoSubmenuDropdown from './list/ChicagoSubmenuDropdown.vue';
import CitationMobileModal from './CitationMobileModal.vue';
import citationFormats, { getAvailableFormats } from '../../js/utils/citationFormats.js';

export default {
    components: {
        ListItemButton,
        ListDropdown,
        ChicagoSubmenuDropdown,
        CitationMobileModal,
    },
    data() {
        return {
            isCitationMenuOpen: false,
            citationWasCopied: false,
            showMobileChicagoSubmenu: false,
        };
    },
    computed: {
        ...mapState(['issue', 'title', 'localizations', 'lang', 'ui']),
        ...mapGetters(['currentLocation', 'isEpub', 'onlyMobileVisible']),
        isMobileModalOpen() {
            return this.ui.citationModal?.opened ?? false;
        },
        /**
         * Returns the filtered Chicago subformats based on issue metadata.
         * @returns {string[]}
         */
        chicagoSubformats() {
            return getAvailableFormats(citationFormats.chicagoSubformats, this.issue, this.title);
        },
        /**
         * Returns the list of available citation formats based on issue metadata.
         * Excludes Chicago when no subformat passes validation.
         * @returns {string[]}
         */
        availableCitationFormats() {
            const formats = getAvailableFormats(citationFormats.formats, this.issue, this.title);

            if (this.chicagoSubformats.length === 0) {
                return formats.filter(format => format !== 'Chicago');
            }

            return formats;
        },
    },
    watch: {
        isCitationMenuOpen(isOpen) {
            if (!isOpen) {
                const chicagoRefs = this.$refs.chicagoDesktopDropdown;
                const chicagoDropdown = Array.isArray(chicagoRefs) ? chicagoRefs[0] : chicagoRefs;
                chicagoDropdown?.hideSubmenu();
            }
        },
        isMobileModalOpen(newVal) {
            if (!newVal) {
                this.showMobileChicagoSubmenu = false;
            }
        },
    },
    beforeDestroy() {
        if (this.isMobileModalOpen) {
            this.closeFeature({ featureKey: 'citationModal' });
        }
    },
    methods: {
        ...mapMutations(['openToast', 'openFeature', 'closeFeature']),
        ...mapActions(['copyToClipBoard', 'trackEvent']),
        toggleCitationMenu() {
            const isMobile = window.innerWidth < 768;

            if (isMobile) {
                if (this.isMobileModalOpen) {
                    this.closeFeature({ featureKey: 'citationModal' });
                } else {
                    this.openFeature({ featureKey: 'citationModal' });
                }
            } else {
                this.isCitationMenuOpen = !this.isCitationMenuOpen;
            }
        },
        closeCitationMenu() {
            this.isCitationMenuOpen = false;
            this.closeFeature({ featureKey: 'citationModal' });
            this.closeFeature({ featureKey: 'rightSubmenu' });
        },
        /**
         * Toggles the mobile Chicago submenu visibility.
         */
        toggleMobileChicagoSubmenu() {
            this.showMobileChicagoSubmenu = !this.showMobileChicagoSubmenu;
        },
        closeModalClickingOutside(event) {
            if (this.$el !== event.target && event.target === event.currentTarget) {
                this.closeCitationMenu();
            }
        },
        /**
         * Generates a book-level citation (without selected text) and copies to clipboard.
         * @param {string} format - The citation format to use
         */
        generateCitation(format) {
            if (this.citationWasCopied) return;

            const citation = citationFormats[format](
                '',
                this.issue,
                this.currentLocation,
                this.title,
                this.isEpub,
                this.localizations[this.lang]
            );
            this.copyToClipBoard(citation);

            this.citationWasCopied = true;
            this.openToast({ text: this.localizations[this.lang].citation.messages.copied });

            this.trackEvent({
                action: `Citation - ${format}`,
                trigger: 'click',
                page: '',
                source: 'toolbar menu',
            });

            this.isCitationMenuOpen = false;
            this.closeFeature({ featureKey: 'citationModal' });
            this.closeFeature({ featureKey: 'rightSubmenu' });

            setTimeout(() => {
                this.citationWasCopied = false;
            }, 1000);
        },
    },
};
</script>

<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.2s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
    opacity: 0;
    transform: translateX(10px);
}
</style>
